/**
 * UsersManagement - Gestión completa de usuarios para admins
 * Permite editar, resetear contraseñas y gestionar roles
 */

import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast/useToast';

export function CompanyAdminUsersManagement() {
  const { companyId, role: currentUserRole } = useAuth();
  const { showToast } = useToast();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(null);

  useEffect(() => {
    loadUsers();
  }, [companyId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // Cargar usuarios de la empresa
      let q;
      if (currentUserRole === 'super_admin') {
        // Super admin ve todos los usuarios
        q = query(collection(db, 'users'));
      } else {
        // Company admin solo ve usuarios de su empresa
        q = query(collection(db, 'users'), where('companyId', '==', companyId));
      }
      
      const snapshot = await getDocs(q);
      const usersData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      console.log('Usuarios cargados:', usersData.length);
      setUsers(usersData);
    } catch (error) {
      console.error('Error cargando usuarios:', error);
      showToast({ type: 'error', message: 'Error cargando usuarios' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({
      ...user,
      displayName: user.displayName || user.name || '',
      email: user.email || '',
      role: user.role || 'company_user',
      active: user.active !== false
    });
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    try {
      const idToken = await auth.currentUser.getIdToken();
      
      // Llamar a Cloud Function para actualizar usuario
      const UPDATE_USER_URL = import.meta.env.VITE_UPDATE_USER_URL || 
        'https://us-central1-firma-digital-db89b.cloudfunctions.net/updateUser';
      
      const response = await fetch(UPDATE_USER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: editingUser.id,
          displayName: editingUser.displayName,
          email: editingUser.email,
          role: editingUser.role,
          companyId: editingUser.companyId,
          active: editingUser.active
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error actualizando usuario');
      }

      showToast({ type: 'success', message: '✅ Usuario actualizado correctamente' });
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      console.error('Error actualizando usuario:', error);
      showToast({ type: 'error', message: error.message });
    }
  };

  const handleResetPassword = (user) => {
    setResettingPassword({
      ...user,
      newPassword: '',
      confirmPassword: '',
      sendEmail: true
    });
  };

  const handleConfirmResetPassword = async () => {
    if (!resettingPassword) return;

    if (resettingPassword.newPassword.length < 6) {
      showToast({ type: 'error', message: 'La contraseña debe tener al menos 6 caracteres' });
      return;
    }

    if (resettingPassword.newPassword !== resettingPassword.confirmPassword) {
      showToast({ type: 'error', message: 'Las contraseñas no coinciden' });
      return;
    }

    try {
      const idToken = await auth.currentUser.getIdToken();
      
      // Llamar a Cloud Function para resetear contraseña
      const RESET_PASSWORD_URL = import.meta.env.VITE_RESET_PASSWORD_URL || 
        'https://us-central1-firma-digital-db89b.cloudfunctions.net/resetUserPassword';
      
      const response = await fetch(RESET_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: resettingPassword.id,
          newPassword: resettingPassword.newPassword,
          sendEmail: resettingPassword.sendEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Error reseteando contraseña');
      }

      const result = await response.json();
      
      if (result.emailSent) {
        showToast({ type: 'success', message: '✅ Contraseña actualizada y email enviado' });
      } else {
        showToast({ type: 'success', message: '✅ Contraseña actualizada' });
      }
      
      setResettingPassword(null);
    } catch (error) {
      console.error('Error reseteando contraseña:', error);
      showToast({ type: 'error', message: error.message });
    }
  };

  const handleToggleActive = async (user) => {
    const newStatus = !user.active;
    
    if (!window.confirm(`¿${newStatus ? 'Activar' : 'Desactivar'} el usuario ${user.displayName || user.email}?`)) {
      return;
    }

    try {
      const idToken = await auth.currentUser.getIdToken();
      
      const UPDATE_USER_URL = import.meta.env.VITE_UPDATE_USER_URL || 
        'https://us-central1-firma-digital-db89b.cloudfunctions.net/updateUser';
      
      const response = await fetch(UPDATE_USER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          uid: user.id,
          active: newStatus
        })
      });

      if (!response.ok) {
        throw new Error('Error actualizando estado del usuario');
      }

      showToast({ type: 'success', message: `Usuario ${newStatus ? 'activado' : 'desactivado'}` });
      await loadUsers();
    } catch (error) {
      console.error('Error actualizando estado:', error);
      showToast({ type: 'error', message: error.message });
    }
  };

  if (loading) {
    return (
      <div className="container">
        <p style={{ padding: 24, textAlign: 'center' }}>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <span className="badge">PANEL ADMIN</span>
      </div>

      <div style={{ marginBottom: '24px' }}>
        <h1 className="title" style={{ marginBottom: '8px' }}>Gestión de Usuarios</h1>
        <p className="meta">Administra los usuarios de tu empresa</p>
      </div>

      <div className="card">
        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <p className="meta">No hay usuarios para mostrar</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {users.map((user) => (
              <div 
                key={user.id} 
                style={{ 
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  transition: 'all 0.2s',
                  opacity: user.active === false ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '20px', flexWrap: 'wrap' }}>
                  {/* Información del usuario */}
                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      {/* Avatar */}
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontWeight: 'bold',
                        fontSize: '18px',
                        flexShrink: 0
                      }}>
                        {(user.displayName || user.name || user.email || '?')[0].toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#0f172a' }}>
                          {user.displayName || user.name || 'Sin nombre'}
                        </h3>
                        <p className="meta" style={{ margin: '4px 0 0', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {user.email}
                        </p>
                      </div>
                    </div>
                    
                    {/* Badges */}
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        background: user.role === 'super_admin' ? '#dbeafe' : user.role === 'company_admin' ? '#dcfce7' : '#fef3c7',
                        color: user.role === 'super_admin' ? '#1e40af' : user.role === 'company_admin' ? '#166534' : '#92400e',
                        border: `1px solid ${user.role === 'super_admin' ? '#bfdbfe' : user.role === 'company_admin' ? '#bbf7d0' : '#fde68a'}`,
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {user.role === 'super_admin' ? 'Super Admin' : user.role === 'company_admin' ? 'Admin' : 'Usuario'}
                      </span>
                      <span style={{ 
                        background: user.active !== false ? '#dcfce7' : '#fee2e2',
                        color: user.active !== false ? '#166534' : '#991b1b',
                        border: `1px solid ${user.active !== false ? '#bbf7d0' : '#fecaca'}`,
                        padding: '4px 10px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: 600,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        {user.active !== false ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    
                    {user.companyId && (
                      <p className="meta" style={{ marginTop: '12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ 
                          background: '#f1f5f9',
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 500
                        }}>
                          Empresa: {user.companyId}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Botones de acción */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                    <button 
                      className="copy"
                      onClick={() => handleEditUser(user)}
                      style={{ 
                        padding: '10px 16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        borderRadius: '8px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Editar
                    </button>
                    <button 
                      className="btn"
                      onClick={() => handleResetPassword(user)}
                      style={{ 
                        padding: '10px 16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        background: '#f59e0b',
                        borderColor: '#fb923c',
                        borderRadius: '8px',
                        transition: 'all 0.2s'
                      }}
                    >
                      Contraseña
                    </button>
                    <button 
                      className="copy"
                      onClick={() => handleToggleActive(user)}
                      style={{ 
                        padding: '10px 16px',
                        fontSize: '13px',
                        fontWeight: 600,
                        borderColor: user.active !== false ? '#ef4444' : '#22c55e',
                        color: user.active !== false ? '#ef4444' : '#22c55e',
                        borderRadius: '8px',
                        transition: 'all 0.2s'
                      }}
                    >
                      {user.active !== false ? 'Desactivar' : 'Activar'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de edición */}
      {editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ margin: '0 0 24px', fontSize: '22px' }}>Editar Usuario</h2>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Nombre
              </label>
              <input
                type="text"
                value={editingUser.displayName}
                onChange={(e) => setEditingUser({ ...editingUser, displayName: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Email
              </label>
              <input
                type="email"
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Rol
              </label>
              <select
                value={editingUser.role}
                onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                style={{ width: '100%' }}
              >
                <option value="company_user">Usuario</option>
                <option value="company_admin">Admin</option>
                {currentUserRole === 'super_admin' && (
                  <option value="super_admin">Super Admin</option>
                )}
              </select>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={editingUser.active}
                  onChange={(e) => setEditingUser({ ...editingUser, active: e.target.checked })}
                />
                <span>Usuario activo</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="copy"
                onClick={() => setEditingUser(null)}
              >
                Cancelar
              </button>
              <button 
                className="btn"
                onClick={handleSaveUser}
              >
                Guardar Cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de reset de contraseña */}
      {resettingPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%'
          }}>
            <h2 style={{ margin: '0 0 8px', fontSize: '22px' }}>Resetear Contraseña</h2>
            <p className="meta" style={{ marginBottom: '24px' }}>
              Usuario: {resettingPassword.displayName || resettingPassword.email}
            </p>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Nueva Contraseña
              </label>
              <input
                type="password"
                value={resettingPassword.newPassword}
                onChange={(e) => setResettingPassword({ ...resettingPassword, newPassword: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Confirmar Contraseña
              </label>
              <input
                type="password"
                value={resettingPassword.confirmPassword}
                onChange={(e) => setResettingPassword({ ...resettingPassword, confirmPassword: e.target.value })}
                placeholder="Repite la contraseña"
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={resettingPassword.sendEmail}
                  onChange={(e) => setResettingPassword({ ...resettingPassword, sendEmail: e.target.checked })}
                />
                <span>Enviar email al usuario con la nueva contraseña</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button 
                className="copy"
                onClick={() => setResettingPassword(null)}
              >
                Cancelar
              </button>
              <button 
                className="btn"
                onClick={handleConfirmResetPassword}
                style={{ background: '#f59e0b', borderColor: '#fb923c' }}
              >
                Resetear Contraseña
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
